import { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { ETL_COPILOT_WEB_SOCKET_URL } from '../../../../BaseUrl';

/**
 * Hook to send { sources, destinations } to mapping WebSocket and collect
 * progress, mapping, and complete messages.
 */
export function useMappingWebSocket() {
  const [progress, setProgress] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [complete, setComplete] = useState(null);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const currentRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (err) {
          console.error('Error closing WebSocket:', err);
        }
        wsRef.current = null;
      }
    };
  }, []);

  const sendPayload = useCallback((payload) => {
    if (!payload || (!payload.sources && !payload.destinations)) return;

    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (err) {
        console.error('Error closing WebSocket:', err);
      }
      wsRef.current = null;
    }

    currentRequestIdRef.current += 1;
    const thisRequestId = currentRequestIdRef.current;

    setLoading(true);
    setProgress(null);
    setMappings([]);
    setComplete(null);

    const hasReceivedMappingRef = { current: false };

    const ws = new WebSocket(`${ETL_COPILOT_WEB_SOCKET_URL}/match-columns`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify(payload));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          setProgress(data);
          // Stop loader only when all mapping messages are done (first progress after we received at least one mapping)
          if (hasReceivedMappingRef.current) {
            setLoading(false);
          }
        } else if (data.type === 'mapping' && data.mapping && typeof data.mapping === 'object') {
          const m = data.mapping;
          if (m.target_column != null || m.mapping_type != null || (Array.isArray(m.source_columns) && m.source_columns.length > 0)) {
            hasReceivedMappingRef.current = true;
            flushSync(() => setMappings((prev) => [...prev, data.mapping]));
          }
        } else if (data.type === 'complete') {
          setComplete(data);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
      }
    };

    ws.onerror = () => {
      if (thisRequestId === currentRequestIdRef.current) {
        setLoading(false);
      }
    };

    ws.onclose = () => {
      // Only update state if this close is for the current request (ignore stale close from previous socket)
      if (thisRequestId !== currentRequestIdRef.current) return;
      wsRef.current = null;
      setLoading(false);
    };
  }, []);

  return {
    progress,
    mappings,
    complete,
    loading,
    sendPayload,
  };
}
